import type { ChapterItem } from '@app/_proto/Protos/chapters';
import { NovelItem } from '@app/_proto/Protos/novels';
import { logSelectChapter, logSelectSeries } from '@app/analytics';
import BlurImg from '@app/domains/common/components/BlurImg';
import useInViewForPosthog from '@app/domains/common/hooks/useInViewForPosthog';
import { getChapterPath, getNovelPath } from '@app/domains/common/utils/path';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import { timestampToUnix } from '@app/libs/utils';
import { useTimeAgo } from '@app/utils/time';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

type Props = {
    chapter: ChapterItem;
};

export default function HomeRecentlyUpdatedChapter({ chapter }: Props) {
    const { slug: chapterSlug, novelInfo, whenToPublish, name: chapterName, spoilerTitle: isSpoiler } = chapter;

    const {
        id: novelId,
        slug: novelSlug,
        coverUrl: novelCoverUrl,
        coverBlurHash: novelCoverBlurHash,
        name: novelName,
        translatorInfo: novelTranslatorInfo,
        translatorName: novelTranslatorName,
    } = novelInfo || {};

    const timeAgo = useTimeAgo(timestampToUnix(whenToPublish), {
        refreshInterval: 0,
    });

    const chapterTitle = `${isSpoiler ? '(Spoiler Title) ' : ''}${chapterName}`;

    const { ref } = useInViewForPosthog({
        event: HomeEvents.ViewCollectionItem,
        triggerOnce: true,
    });

    const handleClickSeries = () => {
        logSelectSeries(
            NovelItem.create({
                id: novelId,
                name: novelName,
                slug: novelSlug,
            })
        );
    };

    const handleClickChapter = () => {
        logSelectChapter(
            NovelItem.create({
                id: novelId,
                name: novelName,
                slug: novelSlug,
            }),
            chapter
        );
    };

    return (
        <section ref={ref}>
            <div className="grid grid-cols-[30px_1fr] gap-10 border-b border-gray-line-base py-8">
                <div>
                    <Link
                        to={getNovelPath(novelSlug ?? '')}
                        onClick={() => handleClickSeries()}
                        data-posthog-click-event={HomeEvents.ClickCollectionItem}
                    >
                        <BlurImg
                            src={novelCoverUrl?.value}
                            blurHash={novelCoverBlurHash?.value}
                            width={30}
                            height={40}
                            innerClassName="rounded-[4px]"
                            alt={novelName}
                            fallbackImage="/cover.png"
                            lazy
                        />
                    </Link>
                </div>
                <div className="md:grid md:grid-cols-[0.5fr_0.5fr_220px_108px] md:gap-12">
                    <div className="font-set-b16 h-19 break-all line-clamp-1 md:font-set-sb15 hover:underline dark:text-white md:h-36 md:text-gray-900 md:line-clamp-2">
                        <Link
                            to={getNovelPath(novelSlug ?? '')}
                            onClick={() => handleClickSeries()}
                            title={novelName}
                            data-posthog-click-event={HomeEvents.ClickCollectionItem}
                        >
                            {novelName}
                        </Link>
                    </div>
                    <div className="font-set-m14 my-2 h-17 break-all text-gray-t3 line-clamp-1 hover:underline md:my-0 md:h-34 md:text-gray-t1 md:line-clamp-2 md:dark:text-gray-t1">
                        <Link
                            onClick={handleClickChapter}
                            to={getChapterPath({ novelSlug, chapterSlug })}
                            className={clsx({
                                'text-spoiler': isSpoiler,
                            })}
                            title={chapterTitle}
                        >
                            {chapterTitle}
                        </Link>
                    </div>
                    <div className="font-set-m14 hidden dark:text-gray-500 md:block md:text-gray-t1 md:dark:text-gray-t1">
                        {novelTranslatorName?.value ?? novelTranslatorInfo?.userInfo?.userName}
                    </div>
                    <div className="font-set-r13 text-gray-desc">
                        <span>{timeAgo}</span>
                        <div className="inline-block md:hidden">
                            <span className="mx-3">&#183;</span>
                            Novel
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
